import { useEffect, useReducer, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Autocomplete, Button, FormControl, Grid, InputLabel, MenuItem, Paper, Select, TextField, ThemeProvider, Typography, createTheme } from '@mui/material';
import { useStyles } from "../../common/appStyles";
import { IGrade } from '../../types/manage';
import { GetAllGrades, GetAllSchools, registerUser } from '../../services/user-service';
import { showSnackbar } from '../../services/snackbar.service';
import { ISchoolDetail } from '../../types/schools';
import { VerifyEmail } from '../../components/HelperComponents/VerifyEmail';
import { LoadingDialog } from '../../components/HelperComponents/LoadingDialog';
import logo from "../../images/YEP - Logo.png";

function reducer(state, action) {
    switch (action.type) {
        case 'SET_FIELD':
            return { ...state, [action.field]: action.value };
        default:
            throw new Error();
    }
}

function isValidEmail(email) {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}

/**
 * @remarks allows a user to enter a name, email, select a grade, and enter a school.
 * It has a button to log in which redirects to Auth0 log in page,
 * and a button to register which will attempt to register the user, and if successful
 * it will open a dialog {@link VerifyEmail}.
 * @returns - html with a form allowing a user to register.
 */
export function Registration() {
    const classes = useStyles();

    const { loginWithRedirect, isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
    const navigate = useNavigate();
    const [schools, setSchools] = useState<ISchoolDetail[]>([]);
    const [filteredSchools, setFilteredSchools] = useState<ISchoolDetail[]>([]);
    const [schoolObject, setSchoolObject] = useState<ISchoolDetail | null>(
        {
            id: 0,
            name: '',
            emailDomainList: [],
            regionId: 0
        });

    const [showVerifyBox, setShowVerifyBox] = useState(false);
    const [loading, setLoading] = useState(false);

    const theme = createTheme({
        palette: {
            primary: {
                main: '#10AD61'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#484A57'
            },
            body1: {
                color: '#00000'
            }
        },
    });

    const [state, dispatch] = useReducer(reducer, {
        firstName: '',
        lastName: '',
        email: '',
        grade: '',
        school: '',
    });

    const { firstName, lastName, email, grade, school } = state;

    const [grades, setGrades] = useState<IGrade[]>([]);

    const setField = (field, value) => {
        dispatch({ type: 'SET_FIELD', field, value });
    };

    const getGrades = () => {
        GetAllGrades()
            .then(response => {
                setGrades(response);
            })
            .catch((error) => {
                showSnackbar('Error geting Grades', 'error', 3000);
            });
    }

    const getSchools = () => {
        GetAllSchools()
            .then(response => {
                setSchools(response);
                setFilteredSchools(response);
                setFilteredSchools((prevArray) => [...prevArray, { id: 0, name: "No School", emailDomainList: [], regionId: 0 }]);
            })
            .catch((error) => {
                showSnackbar('Error getting Schools:' + error, 'error', 3000)
            });
    }

    useEffect(() => {
        getGrades();
        getSchools();
    }, [school]);

    useEffect(() => {
    }, [schoolObject]);

    const register = async (savedUser) => {
        setLoading(true);
        try {
            await registerUser(savedUser);
            setLoading(false);
            setShowVerifyBox(true);
        }
        catch (error: any) {
            switch (error.message) {
                case "Invalid Email Format: Email addresses must harmonize with the {string}@{sld}.{tld} format.":
                    showSnackbar("Invalid Email domain", 'error', 5000);
                    break;
                default:
                    showSnackbar(error.message, 'error', 5000);
            }
        }
        setLoading(false)
    }

    const handleSubmit = (event: any) => {
        let saved = {
            firstName,
            lastName,
            email,
            "currentGrade": grade,
            "roleId": 6,
            "schoolId": schoolObject?.id !== undefined ? schoolObject.id : null,
            "regionId": schoolObject?.regionId
        };

        if (saved.schoolId === 0) {
            saved.schoolId = null;
            saved.regionId = 1;
        }
        register(saved);
    };
    const handleLogin = async () => {
        setShowVerifyBox(false);
        await loginWithRedirect();
    }

    const handleSchool = (event, value) => {
        setSchoolObject(value || null);
    };

    const handleFilterSchools = (email: string) => {
        const emailDomain = email.split('@').pop()
        if (emailDomain) {
            let filtered = schools.filter(school => (school.emailDomainList.join()).toLowerCase().includes(emailDomain.toLowerCase()));
            filtered.push({ id: 0, name: "No School", emailDomainList: [], regionId: 0 });
            setFilteredSchools(filtered);
        }
        else {
            let filtered = schools
            filtered.push({ id: 0, name: "No School", emailDomainList: [], regionId: 0 });
            setFilteredSchools(filtered);
        }
    }

    const isvalid = (
        firstName !== '' &&
        lastName !== '' &&
        isValidEmail(email) &&
        grade !== '' &&
        (schoolObject?.id !== null && schoolObject) &&
        loading !== true
    );

    const handleBack = () => {
        navigate(-1);
    }

    return (
        <div>
            <ThemeProvider theme={theme}>
                <Grid container className={classes.wrapper} justifyContent={"center"} alignItems={"center"} sx={{ backgroundColor: '#484A57' }}>
                    <Grid item xs={12} >
                        <Grid container direction={"column"} alignItems={"center"} rowSpacing={4} sx={{ backgroundColor: '#ffffff' }}>
                            <Grid item>
                                <Typography variant='body1' textAlign={'left'} className={classes.paragraphWidth}>
                                    Those looking to receive the YEC Program Certificate can register using this page.
                                    If you are looking to become a school coordinator or judge, please email us at {"{INSERTEMAILHERE}"}.
                                </Typography>
                            </Grid>
                            <Grid item className={classes.profileGrid} style={{ minWidth: '25%' }}>
                                <Paper elevation={3} style={{ padding: '3rem', width: '100%', boxSizing: 'border-box' }}>
                                    <Grid container direction={"column"} className={classes.gridPadding} rowSpacing={'1vh'}>
                                        <span className="signup-logo">
                                            <Grid container justifyContent={'center'} alignItems={"center"} sx={{ marginBottom: '5vh' }}>
                                                <img src={logo} width={'200vh'} height={'auto'} alt="YEC" onClick={handleBack} />
                                            </Grid>
                                        </span>
                                        <Grid item alignSelf={"center"}>
                                            <Typography sx={{ flex: 1 }} variant="h5" component="div">
                                                Register
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <FormControl fullWidth>
                                                <TextField
                                                    label="First Name"
                                                    value={firstName}
                                                    onChange={(event) => setField('firstName', event.target.value)}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item>
                                            <FormControl fullWidth>
                                                <TextField
                                                    label="Last Name"
                                                    value={lastName}
                                                    onChange={(event) => setField('lastName', event.target.value)}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item>
                                            <FormControl fullWidth>
                                                <TextField
                                                    label="Email"
                                                    value={email}
                                                    onChange={(event) => { setField('email', event.target.value); handleSchool(event, null); handleFilterSchools(event.target.value) }}
                                                />
                                            </FormControl>
                                        </Grid>
                                        <Grid item className={classes.selectTopBotPadding}>
                                            <FormControl fullWidth>
                                                <InputLabel>Grade</InputLabel>
                                                <Select
                                                    label="Grade"
                                                    value={grade}

                                                    MenuProps={{
                                                        disableScrollLock: true,
                                                        PaperProps: {
                                                            style: {
                                                                maxHeight: "15vh",
                                                            },
                                                        }
                                                    }}
                                                    onChange={(event) => setField('grade', event.target.value)}
                                                >
                                                    {grades.map((grade) => (
                                                        <MenuItem key={grade.id} value={grade.id}>
                                                            {grade.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item>
                                            <FormControl fullWidth>
                                                <Autocomplete
                                                    disablePortal
                                                    autoHighlight
                                                    id="combo-box-demo"
                                                    options={filteredSchools}
                                                    onChange={(event, newValue) => { setSchoolObject(newValue) }}
                                                    value={schoolObject}
                                                    getOptionLabel={(option) => option.name}
                                                    renderInput={(params) => <TextField {...params} label="School" />}
                                                />
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid container columnSpacing={3} alignItems={"center"} justifyContent={"center"} direction={"column"} rowSpacing={3}>
                                    <Grid item>
                                        <Button variant="contained" onClick={handleSubmit} disabled={!isvalid}>
                                            register
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Grid container direction={"column"} alignItems={"center"} rowSpacing={'1vh'} className={classes.subWrapper4}>
                                            <Grid item>
                                                <Typography>
                                                    Already have an account?
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <Button variant="outlined" onClick={handleLogin}>
                                                    Login
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    {showVerifyBox && <VerifyEmail
                        showDialog={showVerifyBox}
                        handleSubmit={handleLogin}
                    />}
                    {
                        loading && <LoadingDialog
                            loading={loading} />
                    }
                </Grid>
            </ThemeProvider>
        </div>
    )
}